import React from 'react';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import PaymentIcon from '@mui/icons-material/Payment';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SearchIcon from '@mui/icons-material/Search';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import LinearProgress from '@mui/material/LinearProgress';
import InputAdornment from '@mui/material/InputAdornment';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Collapse from '@mui/material/Collapse';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { buildApiUrl } from './config/api';
import { schooldetails } from './redux/schooldetails';
import { buildPublicApplyUrl } from './utils/publicAdmissionApi';
import {
    formatCurrencyWithSchool,
    getCountryDefaults,
    getCurrencySymbol,
} from './utils/currencyFormatter';

const STATUS_COLORS = {
    payment_pending: 'default',
    submitted: 'info',
    under_review: 'warning',
    approved: 'success',
    rejected: 'error',
    enrolled: 'success',
};

function statusLabel(s) {
    return String(s || '').replace(/_/g, ' ');
}

function normalizeSchoolClassList(raw) {
    if (!raw) return [];
    if (Array.isArray(raw) && raw.length === 1 && Array.isArray(raw[0])) return raw[0];
    return Array.isArray(raw) ? raw : [];
}

function classRowsFromClassDocuments(classes) {
    return normalizeSchoolClassList(classes)
        .filter((cls) => cls && cls.classname)
        .map((cls) => {
            const parsedAmount = parseFloat(cls.admissionFee?.amount);
            return {
                _id: cls._id,
                classname: cls.classname,
                description: cls.admissionFee?.description || 'Admission Fee',
                amount: Number.isFinite(parsedAmount) && parsedAmount > 0 ? parsedAmount : '',
            };
        })
        .sort((a, b) => String(a.classname).localeCompare(String(b.classname)));
}

function parseFeeAmount(value) {
    const n = parseFloat(String(value ?? '').replace(/,/g, '').trim());
    return Number.isFinite(n) && n > 0 ? n : null;
}

function savedFeeString(row) {
    return row.amount !== '' && row.amount != null ? String(row.amount) : '';
}

function isClassFeeConfigured(row, drafts) {
    const draft = String(drafts[row._id] ?? '').trim();
    const saved = savedFeeString(row);
    return parseFeeAmount(draft) != null || parseFeeAmount(saved) != null;
}

function isClassFeeDirty(row, drafts) {
    const draft = String(drafts[row._id] ?? '').trim();
    const saved = savedFeeString(row);
    if (!draft && !saved) return false;
    return draft !== saved;
}

function formatAdmissionFee(amount, countryCode) {
    const defaults = getCountryDefaults(countryCode);
    return formatCurrencyWithSchool(amount, [
        { country: defaults.country, currency: defaults.currency, locale: defaults.locale },
    ]);
}

function AdmissionFeeConfiguration({
    schoolID,
    classFees,
    loading,
    error,
    drafts,
    onDraftChange,
    savingId,
    bulkSaving,
    onRefresh,
    onSaveRow,
    onSaveAll,
    onApplyToUnset,
    currencySymbol,
    currencyCode,
}) {
    const [search, setSearch] = React.useState('');
    const [filter, setFilter] = React.useState('all');
    const [bulkAmount, setBulkAmount] = React.useState('');
    const [tableExpanded, setTableExpanded] = React.useState(true);

    const configuredCount = classFees.filter((row) => isClassFeeConfigured(row, drafts)).length;
    const totalCount = classFees.length;
    const missingCount = totalCount - configuredCount;
    const dirtyCount = classFees.filter((row) => isClassFeeDirty(row, drafts)).length;
    const progress = totalCount > 0 ? Math.round((configuredCount / totalCount) * 100) : 0;

    const filteredRows = classFees.filter((row) => {
        const name = String(row.classname || '').toLowerCase();
        const q = search.trim().toLowerCase();
        if (q && !name.includes(q)) return false;
        if (filter === 'configured') return isClassFeeConfigured(row, drafts);
        if (filter === 'missing') return !isClassFeeConfigured(row, drafts);
        return true;
    });

    const handleApplyBulk = () => {
        const amt = parseFeeAmount(bulkAmount);
        if (amt == null) return;
        onApplyToUnset(amt);
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, sm: 2.5 },
                mb: 3,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                background: 'linear-gradient(180deg, rgba(23, 96, 211, 0.06) 0%, rgba(255,255,255,1) 120px)',
            }}
        >
            <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ mb: 2 }}>
                <Box
                    sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        flexShrink: 0,
                    }}
                >
                    <PaymentsOutlinedIcon />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                        Admission fee configuration
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        One-time application fee per class for online admissions — not part of the school fees bill.
                    </Typography>
                </Box>
                <Button variant="outlined" size="small" onClick={onRefresh} disabled={loading || bulkSaving}>
                    Refresh
                </Button>
            </Stack>

            {error ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            ) : null}

            {!schoolID ? (
                <Alert severity="error">School ID not found. Log out and log in again.</Alert>
            ) : loading ? (
                <Box sx={{ py: 4 }}>
                    <LinearProgress sx={{ mb: 1 }} />
                    <Typography variant="body2" color="text.secondary" align="center">
                        Loading classes…
                    </Typography>
                </Box>
            ) : totalCount === 0 ? (
                <Alert severity="info" icon={<SchoolOutlinedIcon />}>
                    No classes yet. Add classes under <strong>Class</strong>, then set an admission fee for each here.
                </Alert>
            ) : (
                <>
                    <Grid container spacing={1.5} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={4}>
                            <Box
                                sx={{
                                    p: 1.5,
                                    borderRadius: 2,
                                    bgcolor: 'background.paper',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                }}
                            >
                                <Typography variant="caption" color="text.secondary">
                                    Classes
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                    {totalCount}
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <Box
                                sx={{
                                    p: 1.5,
                                    borderRadius: 2,
                                    bgcolor: (theme) => alpha(theme.palette.success.main, 0.1),
                                    border: '1px solid',
                                    borderColor: (theme) => alpha(theme.palette.success.main, 0.35),
                                }}
                            >
                                <Typography variant="caption" color="success.dark">
                                    Configured
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.dark' }}>
                                    {configuredCount}
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <Box
                                sx={{
                                    p: 1.5,
                                    borderRadius: 2,
                                    bgcolor: (theme) =>
                                        missingCount > 0
                                            ? alpha(theme.palette.warning.main, 0.1)
                                            : theme.palette.grey[50],
                                    border: '1px solid',
                                    borderColor: (theme) =>
                                        missingCount > 0
                                            ? alpha(theme.palette.warning.main, 0.35)
                                            : theme.palette.divider,
                                }}
                            >
                                <Typography variant="caption" color={missingCount > 0 ? 'warning.dark' : 'text.secondary'}>
                                    Not set
                                </Typography>
                                <Typography
                                    variant="h5"
                                    sx={{ fontWeight: 700, color: missingCount > 0 ? 'warning.dark' : 'text.primary' }}
                                >
                                    {missingCount}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>

                    <Box sx={{ mb: 2 }}>
                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                                Setup progress
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {progress}%
                            </Typography>
                        </Stack>
                        <LinearProgress
                            variant="determinate"
                            value={progress}
                            sx={{ height: 8, borderRadius: 4 }}
                        />
                    </Box>

                    {missingCount > 0 && (
                        <Alert severity="warning" sx={{ mb: 2 }} icon={<ErrorOutlineIcon />}>
                            {missingCount} class{missingCount === 1 ? '' : 'es'} still need an admission fee before
                            parents can apply for that class online.
                        </Alert>
                    )}

                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={1.5}
                        alignItems={{ xs: 'stretch', md: 'center' }}
                        sx={{ mb: 2 }}
                    >
                        <TextField
                            size="small"
                            placeholder="Search class…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" color="action" />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ minWidth: { md: 200 }, flex: { md: 1 } }}
                        />
                        <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={filter}
                            onChange={(_, val) => val && setFilter(val)}
                        >
                            <ToggleButton value="all">All ({totalCount})</ToggleButton>
                            <ToggleButton value="configured">Configured ({configuredCount})</ToggleButton>
                            <ToggleButton value="missing">Not set ({missingCount})</ToggleButton>
                        </ToggleButtonGroup>
                    </Stack>

                    <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2, bgcolor: 'grey.50' }}>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                            Quick fill — apply the same fee to all classes that are not set yet
                        </Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                            <TextField
                                size="small"
                                type="number"
                                label="Amount for unset classes"
                                value={bulkAmount}
                                onChange={(e) => setBulkAmount(e.target.value)}
                                inputProps={{ min: 0, step: '0.01' }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">{currencySymbol}</InputAdornment>
                                    ),
                                }}
                                sx={{ maxWidth: 220 }}
                            />
                            <Button
                                variant="outlined"
                                size="small"
                                disabled={parseFeeAmount(bulkAmount) == null || missingCount === 0}
                                onClick={handleApplyBulk}
                            >
                                Apply to {missingCount} unset
                            </Button>
                            {dirtyCount > 0 && (
                                <Button
                                    variant="contained"
                                    size="small"
                                    disabled={bulkSaving || savingId}
                                    onClick={onSaveAll}
                                    sx={{ ml: { sm: 'auto' } }}
                                >
                                    {bulkSaving ? 'Saving…' : `Save ${dirtyCount} change${dirtyCount === 1 ? '' : 's'}`}
                                </Button>
                            )}
                        </Stack>
                    </Paper>

                    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                        <Box
                            role="button"
                            tabIndex={0}
                            onClick={() => setTableExpanded((open) => !open)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setTableExpanded((open) => !open);
                                }
                            }}
                            sx={{
                                px: 2,
                                py: 1.25,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                cursor: 'pointer',
                                bgcolor: 'grey.50',
                                borderBottom: tableExpanded ? '1px solid' : 'none',
                                borderColor: 'divider',
                                '&:hover': { bgcolor: 'action.hover' },
                            }}
                        >
                            <IconButton
                                size="small"
                                aria-label={tableExpanded ? 'Collapse class list' : 'Expand class list'}
                                sx={{
                                    transform: tableExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.2s',
                                }}
                            >
                                <ExpandMoreIcon />
                            </IconButton>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                    Class fees
                                </Typography>
                                <Typography variant="caption" color="text.secondary" noWrap>
                                    {filteredRows.length === totalCount
                                        ? `${totalCount} class${totalCount === 1 ? '' : 'es'}`
                                        : `${filteredRows.length} of ${totalCount} classes`}
                                    {missingCount > 0 ? ` · ${missingCount} not set` : ''}
                                </Typography>
                            </Box>
                            {dirtyCount > 0 && (
                                <Chip size="small" color="primary" label={`${dirtyCount} unsaved`} />
                            )}
                        </Box>

                        <Collapse in={tableExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ p: { xs: 1, sm: 1.5 } }}>
                                {filteredRows.length === 0 ? (
                                    <Alert severity="info" sx={{ m: 0 }}>
                                        No classes match your search or filter.
                                    </Alert>
                                ) : (
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 700 }}>Class</TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                                    <TableCell sx={{ fontWeight: 700, minWidth: 180 }}>
                                                        Admission fee
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 700, width: 120 }}>
                                                        Action
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {filteredRows.map((row) => {
                                                    const configured = isClassFeeConfigured(row, drafts);
                                                    const dirty = isClassFeeDirty(row, drafts);
                                                    const invalid =
                                                        String(drafts[row._id] ?? '').trim() !== '' &&
                                                        parseFeeAmount(drafts[row._id]) == null;
                                                    const isSaving = savingId === row._id;

                                                    return (
                                                        <TableRow
                                                            key={row._id}
                                                            hover
                                                            sx={{
                                                                bgcolor: (theme) =>
                                                                    !configured
                                                                        ? alpha(theme.palette.warning.main, 0.08)
                                                                        : dirty
                                                                          ? theme.palette.action.hover
                                                                          : 'inherit',
                                                            }}
                                                        >
                                                            <TableCell>
                                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                    {row.classname}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    size="small"
                                                                    icon={
                                                                        configured ? (
                                                                            <CheckCircleOutlineIcon />
                                                                        ) : (
                                                                            <ErrorOutlineIcon />
                                                                        )
                                                                    }
                                                                    label={configured ? 'Configured' : 'Not set'}
                                                                    color={configured ? 'success' : 'warning'}
                                                                    variant={configured ? 'filled' : 'outlined'}
                                                                />
                                                                {dirty && (
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="primary"
                                                                        display="block"
                                                                        sx={{ mt: 0.25 }}
                                                                    >
                                                                        Unsaved
                                                                    </Typography>
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                <TextField
                                                                    size="small"
                                                                    type="number"
                                                                    fullWidth
                                                                    error={invalid}
                                                                    helperText={invalid ? 'Enter a valid amount' : ' '}
                                                                    FormHelperTextProps={{ sx: { m: 0, minHeight: 16 } }}
                                                                    inputProps={{ min: 0, step: '0.01' }}
                                                                    value={drafts[row._id] ?? ''}
                                                                    onChange={(e) =>
                                                                        onDraftChange(row._id, e.target.value)
                                                                    }
                                                                    placeholder="0.00"
                                                                    InputProps={{
                                                                        startAdornment: (
                                                                            <InputAdornment position="start">
                                                                                {currencySymbol}
                                                                            </InputAdornment>
                                                                        ),
                                                                    }}
                                                                    sx={{ maxWidth: 200 }}
                                                                />
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <Button
                                                                    size="small"
                                                                    variant={dirty ? 'contained' : 'outlined'}
                                                                    disabled={
                                                                        isSaving ||
                                                                        bulkSaving ||
                                                                        !dirty ||
                                                                        parseFeeAmount(drafts[row._id]) == null
                                                                    }
                                                                    onClick={() => onSaveRow(row)}
                                                                >
                                                                    {isSaving ? 'Saving…' : 'Save'}
                                                                </Button>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </Box>
                        </Collapse>
                    </Paper>
                </>
            )}
        </Paper>
    );
}

function formatDisplayDate(value) {
    if (!value) return '—';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString();
}

function formatDisplayDateTime(value) {
    if (!value) return '—';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
}

function displayValue(value) {
    const text = String(value ?? '').trim();
    return text || '—';
}

function DetailSection({ title, icon, children }) {
    return (
        <Box sx={{ mb: 2.5 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
                {icon}
                <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: 0.2 }}>
                    {title}
                </Typography>
            </Stack>
            {children}
        </Box>
    );
}

function DetailField({ label, value, fullWidth }) {
    if (!value || value === '—') return null;
    return (
        <Grid item xs={12} sm={fullWidth ? 12 : 6}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
                {label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
                {value}
            </Typography>
        </Grid>
    );
}

function ApplicationDetailDialog({
    open,
    application,
    loading,
    reviewNotes,
    onReviewNotesChange,
    actionLoading,
    onClose,
    onMarkUnderReview,
    onReject,
    onApprove,
    onEnroll,
    onCopyText,
    formatFee,
}) {
    if (!application && !loading) return null;

    const studentName = application
        ? `${application.lastname || ''} ${application.othernames || ''}`.trim()
        : '';
    const guardianName = application
        ? `${application.guardianlastname || ''} ${application.guardianothernames || ''}`.trim()
        : '';
    const motherName = application
        ? `${application.motherlastname || ''} ${application.motherothernames || ''}`.trim()
        : '';
    const fatherName = application
        ? `${application.fatherlastname || ''} ${application.fatherothernames || ''}`.trim()
        : '';
    const hasParentInfo =
        motherName || fatherName || application?.mothercontact || application?.fathercontact;
    const canReview = application && ['submitted', 'under_review'].includes(application.status);
    const canEnroll = application && application.status === 'approved';
    const notesDisabled =
        application && (application.status === 'enrolled' || application.status === 'rejected');

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            scroll="paper"
            PaperProps={{ sx: { borderRadius: 2 } }}
        >
            <DialogTitle sx={{ pr: 6, pb: 1 }}>
                <Stack spacing={1}>
                    <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
                        <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
                            {loading ? 'Loading application…' : studentName || 'Application'}
                        </Typography>
                        {application?.status && (
                            <Chip
                                size="small"
                                label={statusLabel(application.status)}
                                color={STATUS_COLORS[application.status] || 'default'}
                            />
                        )}
                    </Stack>
                    {application && (
                        <Typography variant="body2" color="text.secondary">
                            {application.applicationNumber}
                            {application.academicYear ? ` · ${application.academicYear}` : ''}
                            {application.createdAt
                                ? ` · Submitted ${formatDisplayDateTime(application.createdAt)}`
                                : ''}
                        </Typography>
                    )}
                </Stack>
                <IconButton
                    aria-label="Close"
                    onClick={onClose}
                    sx={{ position: 'absolute', right: 12, top: 12 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ bgcolor: 'grey.50', pt: 2 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : application ? (
                    <Stack spacing={2}>
                        {application.enrollment?.studentID && (
                            <Alert
                                severity="success"
                                icon={<CheckCircleOutlineIcon fontSize="inherit" />}
                                sx={{ borderRadius: 1.5 }}
                            >
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                    Enrolled successfully
                                </Typography>
                                <Typography variant="body2">
                                    Student ID: <strong>{application.enrollment.studentID}</strong>
                                    {application.enrollment.enrolledAt &&
                                        ` · ${formatDisplayDateTime(application.enrollment.enrolledAt)}`}
                                </Typography>
                            </Alert>
                        )}

                        {application.status === 'rejected' && application.review?.rejectionReason && (
                            <Alert severity="error" sx={{ borderRadius: 1.5 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                    Rejection reason
                                </Typography>
                                <Typography variant="body2">{application.review.rejectionReason}</Typography>
                            </Alert>
                        )}

                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper' }}>
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={2}
                                divider={<Divider flexItem orientation="vertical" />}
                            >
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Class applied
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                                        {displayValue(application.classApplied)}
                                    </Typography>
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Admission fee paid
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 700, color: 'primary.main' }}>
                                        {formatFee(application.feeGhs || 0)}
                                    </Typography>
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Guardian phone
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        {displayValue(application.guardiancontact)}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Paper>

                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper' }}>
                            <DetailSection
                                title="Student details"
                                icon={<PersonOutlineIcon fontSize="small" color="primary" />}
                            >
                                <Grid container spacing={2}>
                                    <DetailField label="Full name" value={studentName} fullWidth />
                                    <DetailField label="Gender" value={displayValue(application.gender)} />
                                    <DetailField label="Date of birth" value={displayValue(application.dob)} />
                                    <DetailField label="Hometown" value={displayValue(application.hometown)} />
                                    <DetailField label="Residence" value={displayValue(application.residence)} />
                                    <DetailField label="Religion" value={displayValue(application.religion)} />
                                    <DetailField
                                        label="BECE / index no."
                                        value={displayValue(application.studentIndex)}
                                    />
                                </Grid>
                            </DetailSection>

                            <Divider sx={{ my: 2 }} />

                            <DetailSection
                                title="Guardian"
                                icon={<FamilyRestroomIcon fontSize="small" color="primary" />}
                            >
                                <Grid container spacing={2}>
                                    <DetailField label="Name" value={guardianName} fullWidth />
                                    <DetailField
                                        label="Relationship"
                                        value={displayValue(application.guardianRelationship)}
                                    />
                                    <DetailField label="Phone" value={displayValue(application.guardiancontact)} />
                                    <DetailField label="Email" value={displayValue(application.guardianemail)} />
                                    <DetailField
                                        label="Occupation"
                                        value={displayValue(application.guardianoccupation)}
                                    />
                                    <DetailField
                                        label="Residence"
                                        value={displayValue(application.guardianresidence)}
                                    />
                                    <DetailField
                                        label="Applicant email (receipt)"
                                        value={displayValue(application.applicantEmail)}
                                        fullWidth
                                    />
                                </Grid>
                            </DetailSection>

                            {hasParentInfo && (
                                <>
                                    <Divider sx={{ my: 2 }} />
                                    <DetailSection title="Parents" icon={<FamilyRestroomIcon fontSize="small" />}>
                                        <Grid container spacing={2}>
                                            <DetailField label="Mother" value={motherName} />
                                            <DetailField
                                                label="Mother phone"
                                                value={displayValue(application.mothercontact)}
                                            />
                                            <DetailField label="Father" value={fatherName} />
                                            <DetailField
                                                label="Father phone"
                                                value={displayValue(application.fathercontact)}
                                            />
                                        </Grid>
                                    </DetailSection>
                                </>
                            )}

                            {application.payment?.paystackReference && (
                                <>
                                    <Divider sx={{ my: 2 }} />
                                    <DetailSection
                                        title="Payment"
                                        icon={<PaymentIcon fontSize="small" color="success" />}
                                    >
                                        <Grid container spacing={2}>
                                            <DetailField
                                                label="Amount"
                                                value={formatFee(
                                                    application.payment.amountGhs || application.feeGhs || 0
                                                )}
                                            />
                                            <DetailField
                                                label="Paid on"
                                                value={formatDisplayDateTime(application.payment.paidAt)}
                                            />
                                            <DetailField
                                                label="Channel"
                                                value={displayValue(application.payment.channel)}
                                            />
                                            <Grid item xs={12}>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    display="block"
                                                    sx={{ mb: 0.25 }}
                                                >
                                                    Paystack reference
                                                </Typography>
                                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ fontFamily: 'monospace', fontWeight: 500 }}
                                                    >
                                                        {application.payment.paystackReference}
                                                    </Typography>
                                                    <IconButton
                                                        size="small"
                                                        aria-label="Copy payment reference"
                                                        onClick={() =>
                                                            onCopyText(application.payment.paystackReference)
                                                        }
                                                    >
                                                        <ContentCopyIcon fontSize="inherit" />
                                                    </IconButton>
                                                </Stack>
                                            </Grid>
                                        </Grid>
                                    </DetailSection>
                                </>
                            )}

                            {(application.review?.reviewedAt ||
                                application.review?.reviewedBy ||
                                application.enrollment?.enrolledBy) && (
                                <>
                                    <Divider sx={{ my: 2 }} />
                                    <DetailSection
                                        title="Review & enrollment"
                                        icon={<SchoolOutlinedIcon fontSize="small" />}
                                    >
                                        <Grid container spacing={2}>
                                            <DetailField
                                                label="Reviewed by"
                                                value={displayValue(application.review?.reviewedBy)}
                                            />
                                            <DetailField
                                                label="Reviewed on"
                                                value={formatDisplayDateTime(application.review?.reviewedAt)}
                                            />
                                            <DetailField
                                                label="Enrolled by"
                                                value={displayValue(application.enrollment?.enrolledBy)}
                                            />
                                            <DetailField
                                                label="Enrolled on"
                                                value={formatDisplayDateTime(application.enrollment?.enrolledAt)}
                                            />
                                        </Grid>
                                    </DetailSection>
                                </>
                            )}
                        </Paper>

                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                Internal review notes
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                Visible to staff only — not shown to parents.
                            </Typography>
                            <TextField
                                placeholder="Add notes about this application…"
                                multiline
                                minRows={3}
                                value={reviewNotes}
                                onChange={(e) => onReviewNotesChange(e.target.value)}
                                fullWidth
                                disabled={notesDisabled}
                                size="small"
                            />
                        </Paper>
                    </Stack>
                ) : null}
            </DialogContent>

            <DialogActions
                sx={{
                    px: 3,
                    py: 2,
                    flexWrap: 'wrap',
                    gap: 1,
                    justifyContent: 'space-between',
                }}
            >
                <Button onClick={onClose} color="inherit">
                    Close
                </Button>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {canReview && (
                        <>
                            <Button onClick={onMarkUnderReview} disabled={actionLoading} variant="outlined">
                                Mark under review
                            </Button>
                            <Button onClick={onReject} disabled={actionLoading} color="error" variant="outlined">
                                Reject
                            </Button>
                            <Button
                                variant="contained"
                                color="success"
                                onClick={onApprove}
                                disabled={actionLoading}
                            >
                                Approve
                            </Button>
                        </>
                    )}
                    {canEnroll && (
                        <Button variant="contained" onClick={onEnroll} disabled={actionLoading}>
                            Enroll student
                        </Button>
                    )}
                </Stack>
            </DialogActions>
        </Dialog>
    );
}

export default function AdmissionApplications() {
    const dispatch = useDispatch();
    const admin = useSelector((state) => state.admin) || {};
    const schooldetails_ = useSelector((state) => state.schooldetails) || [];
    const classState = useSelector((state) => state.class) || [];
    const school = schooldetails_[0] || {};

    const [rows, setRows] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [statusFilter, setStatusFilter] = React.useState('all');
    const [selected, setSelected] = React.useState(null);
    const [detailOpen, setDetailOpen] = React.useState(false);
    const [detailLoading, setDetailLoading] = React.useState(false);
    const [rejectOpen, setRejectOpen] = React.useState(false);
    const [rejectReason, setRejectReason] = React.useState('');
    const [reviewNotes, setReviewNotes] = React.useState('');
    const [snack, setSnack] = React.useState(null);
    const [actionLoading, setActionLoading] = React.useState(false);

    const [admissionsOpen, setAdmissionsOpen] = React.useState(false);
    const [academicYear, setAcademicYear] = React.useState('');
    const [country, setCountry] = React.useState('GH');
    const [settingsSaving, setSettingsSaving] = React.useState(false);
    const [classFees, setClassFees] = React.useState([]);
    const [classFeesLoading, setClassFeesLoading] = React.useState(false);
    const [classFeesError, setClassFeesError] = React.useState('');
    const [classFeeDrafts, setClassFeeDrafts] = React.useState({});
    const [classFeeSavingId, setClassFeeSavingId] = React.useState('');
    const [classFeeBulkSaving, setClassFeeBulkSaving] = React.useState(false);

    const schoolID = admin.schoolID || school.schoolID;
    const [applyUrl, setApplyUrl] = React.useState('');

    React.useEffect(() => {
        if (schoolID) {
            setApplyUrl(buildPublicApplyUrl(schoolID));
        }
    }, [schoolID]);

    React.useEffect(() => {
        if (school) {
            setAdmissionsOpen(school.admissionsOpen === true);
            setAcademicYear(school.admissionAcademicYear || '');
            setCountry(school.country || 'GH');
        }
    }, [school]);

    const countryDefaults = React.useMemo(() => getCountryDefaults(country), [country]);
    const currencyCode = countryDefaults.currency;
    const currencySymbol = React.useMemo(() => getCurrencySymbol(currencyCode), [currencyCode]);
    const formatFee = React.useCallback(
        (amount) => formatAdmissionFee(amount, country),
        [country]
    );

    const loadApplications = React.useCallback(async () => {
        if (!schoolID) return;
        setLoading(true);
        try {
            const res = await axios.post(buildApiUrl('/api/admissions/list'), {
                schoolID,
                status: statusFilter,
            });
            setRows(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            setSnack(err.response?.data?.message || err.message || 'Failed to load applications');
        } finally {
            setLoading(false);
        }
    }, [schoolID, statusFilter]);

    React.useEffect(() => {
        loadApplications();
    }, [loadApplications]);

    const loadClassFees = React.useCallback(async () => {
        if (!schoolID) return;
        setClassFeesLoading(true);
        setClassFeesError('');
        try {
            let list = [];
            try {
                const res = await axios.post(buildApiUrl('/api/admissions/admin/class-fees'), { schoolID });
                list = Array.isArray(res.data) ? res.data : [];
            } catch (apiErr) {
                let classes = normalizeSchoolClassList(classState);
                if (classes.length === 0) {
                    const classRes = await axios.post(buildApiUrl('/api/class/getclassdata/'), { schoolID });
                    classes = Array.isArray(classRes.data) ? classRes.data : [];
                }
                list = classRowsFromClassDocuments(classes);
                const status = apiErr.response?.status;
                if (list.length === 0) throw apiErr;
                if (status === 404 || status === 405) {
                    setClassFeesError(
                        'Showing your school classes below. Deploy the latest Shared-Admin-server to enable saving admission fees.'
                    );
                }
            }
            setClassFees(list);
            const drafts = {};
            for (const row of list) {
                drafts[row._id] = row.amount !== '' && row.amount != null ? String(row.amount) : '';
            }
            setClassFeeDrafts(drafts);
        } catch (err) {
            setClassFees([]);
            setClassFeesError(err.response?.data?.message || err.message || 'Could not load classes');
        } finally {
            setClassFeesLoading(false);
        }
    }, [schoolID, classState]);

    React.useEffect(() => {
        loadClassFees();
    }, [loadClassFees]);

    const persistClassFee = async (row, amount) => {
        await axios.post(buildApiUrl('/api/admissions/admin/class-fee'), {
            schoolID,
            classId: row._id,
            classname: row.classname,
            amount,
            description: row.description || 'Admission Fee',
        });
    };

    const saveClassFee = async (row) => {
        if (!schoolID || !row) return;
        const amount = parseFeeAmount(classFeeDrafts[row._id]);
        if (amount == null) {
            setSnack('Enter a valid admission fee amount greater than 0');
            return;
        }
        setClassFeeSavingId(row._id);
        try {
            await persistClassFee(row, amount);
            setSnack(`Admission fee saved for ${row.classname}`);
            await loadClassFees();
        } catch (err) {
            setSnack(err.response?.data?.message || err.message || 'Failed to save admission fee');
        } finally {
            setClassFeeSavingId('');
        }
    };

    const saveAllClassFees = async () => {
        const toSave = classFees.filter((row) => {
            if (!isClassFeeDirty(row, classFeeDrafts)) return false;
            return parseFeeAmount(classFeeDrafts[row._id]) != null;
        });
        if (toSave.length === 0) {
            setSnack('No valid unsaved changes to save');
            return;
        }
        setClassFeeBulkSaving(true);
        let saved = 0;
        try {
            for (const row of toSave) {
                try {
                    await persistClassFee(row, parseFeeAmount(classFeeDrafts[row._id]));
                    saved += 1;
                } catch (err) {
                    setSnack(
                        err.response?.data?.message ||
                            err.message ||
                            `Failed to save fee for ${row.classname}`
                    );
                    break;
                }
            }
            if (saved > 0) {
                setSnack(`Saved admission fees for ${saved} class${saved === 1 ? '' : 'es'}`);
                await loadClassFees();
            }
        } finally {
            setClassFeeBulkSaving(false);
        }
    };

    const applyFeeToUnsetClasses = (amount) => {
        const next = { ...classFeeDrafts };
        for (const row of classFees) {
            if (!isClassFeeConfigured(row, classFeeDrafts)) {
                next[row._id] = String(amount);
            }
        }
        setClassFeeDrafts(next);
    };

    const openDetail = async (row) => {
        setDetailOpen(true);
        setDetailLoading(true);
        setSelected(null);
        try {
            const res = await axios.post(buildApiUrl('/api/admissions/detail'), {
                schoolID,
                id: row._id,
            });
            setSelected(res.data);
            setReviewNotes(res.data?.review?.notes || '');
        } catch (err) {
            setDetailOpen(false);
            setSnack(err.response?.data?.message || err.message || 'Failed to load detail');
        } finally {
            setDetailLoading(false);
        }
    };

    const closeDetail = () => {
        setDetailOpen(false);
        setSelected(null);
    };

    const copyText = (text) => {
        if (!text) return;
        navigator.clipboard.writeText(String(text)).then(() => setSnack('Copied to clipboard'));
    };

    const runReview = async (action, extra = {}) => {
        if (!selected) return;
        setActionLoading(true);
        try {
            const res = await axios.post(buildApiUrl('/api/admissions/review'), {
                schoolID,
                id: selected._id,
                action,
                notes: reviewNotes,
                rejectionReason: extra.rejectionReason,
                reviewerName: `${admin.lastname || ''} ${admin.othernames || ''}`.trim(),
                reviewerEmail: admin.email || admin.username || '',
            });
            setSelected(res.data.application);
            setSnack(`Application ${action === 'approve' ? 'approved' : action === 'reject' ? 'rejected' : 'updated'}`);
            setRejectOpen(false);
            loadApplications();
        } catch (err) {
            setSnack(err.response?.data?.message || err.message || 'Review failed');
        } finally {
            setActionLoading(false);
        }
    };

    const runEnroll = async () => {
        if (!selected) return;
        setActionLoading(true);
        try {
            const res = await axios.post(buildApiUrl('/api/admissions/enroll'), {
                schoolID,
                id: selected._id,
                reviewerName: `${admin.lastname || ''} ${admin.othernames || ''}`.trim(),
                reviewerEmail: admin.email || admin.username || '',
            });
            setSelected(res.data.application);
            setSnack(
                res.data.student
                    ? `Enrolled — Student ID: ${res.data.student.ID || res.data.application?.enrollment?.studentID}`
                    : 'Enrolled'
            );
            loadApplications();
        } catch (err) {
            setSnack(err.response?.data?.message || err.message || 'Enrollment failed');
        } finally {
            setActionLoading(false);
        }
    };

    const saveSettings = async () => {
        if (!schoolID) return;
        setSettingsSaving(true);
        try {
            const res = await axios.post(buildApiUrl('/api/admissions/admin/settings'), {
                schoolID,
                admissionsOpen,
                admissionAcademicYear: academicYear,
                country,
                currency: countryDefaults.currency,
                locale: countryDefaults.locale,
            });
            if (Array.isArray(res.data) && res.data.length > 0) {
                dispatch(schooldetails({ data: res.data }));
            }
            setSnack('Admission settings saved');
        } catch (err) {
            setSnack(err.response?.data?.message || err.message || 'Could not save settings');
        } finally {
            setSettingsSaving(false);
        }
    };

    const copyLink = () => {
        navigator.clipboard.writeText(applyUrl).then(() => setSnack('Apply link copied'));
    };

    return (
        <Box sx={{ p: { xs: 1, sm: 2 } }}>
            <Typography variant="h5" gutterBottom>
                Admission Applications
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Configure admission fees per class, then open public applications on{' '}
                <strong>sisrevolution.com</strong>. Admission fees are separate from the class term bill (school fees).
            </Typography>

            <AdmissionFeeConfiguration
                schoolID={schoolID}
                classFees={classFees}
                loading={classFeesLoading}
                error={classFeesError}
                drafts={classFeeDrafts}
                onDraftChange={(id, value) =>
                    setClassFeeDrafts((prev) => ({
                        ...prev,
                        [id]: value,
                    }))
                }
                savingId={classFeeSavingId}
                bulkSaving={classFeeBulkSaving}
                onRefresh={loadClassFees}
                onSaveRow={saveClassFee}
                onSaveAll={saveAllClassFees}
                onApplyToUnset={applyFeeToUnsetClasses}
                currencySymbol={currencySymbol}
                currencyCode={currencyCode}
            />

            <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
                    Public admissions
                </Typography>
                <FormControlLabel
                    control={
                        <Switch
                            checked={admissionsOpen}
                            onChange={(e) => setAdmissionsOpen(e.target.checked)}
                        />
                    }
                    label="Accept online applications"
                />
                <TextField
                    label="Academic year (shown on form)"
                    value={academicYear}
                    onChange={(e) => setAcademicYear(e.target.value)}
                    size="small"
                    sx={{ display: 'block', mt: 1, mb: 1, maxWidth: 320 }}
                />
                <FormControl size="small" sx={{ display: 'block', mb: 1, maxWidth: 320 }}>
                    <InputLabel id="admission-country-label">Country</InputLabel>
                    <Select
                        labelId="admission-country-label"
                        label="Country"
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                    >
                        <MenuItem value="GH">Ghana</MenuItem>
                        <MenuItem value="LR">Liberia</MenuItem>
                    </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Admission fees use <strong>{currencyCode}</strong> ({currencySymbol}) for this country.
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                    <TextField
                        label="Public apply link"
                        value={applyUrl || (schoolID ? 'Generating…' : 'Log in — school ID missing')}
                        size="small"
                        InputProps={{ readOnly: true }}
                        sx={{ flex: 1, minWidth: 260 }}
                    />
                    <IconButton onClick={copyLink} aria-label="Copy link" disabled={!applyUrl}>
                        <ContentCopyIcon />
                    </IconButton>
                    <Button
                        variant="outlined"
                        disabled={!applyUrl}
                        onClick={() => applyUrl && window.open(applyUrl, '_blank', 'noopener,noreferrer')}
                    >
                        Open link
                    </Button>
                    <Button variant="contained" onClick={saveSettings} disabled={settingsSaving}>
                        {settingsSaving ? 'Saving…' : 'Save settings'}
                    </Button>
                </Box>
            </Paper>

            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                        value={statusFilter}
                        label="Status"
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="submitted">Submitted</MenuItem>
                        <MenuItem value="under_review">Under review</MenuItem>
                        <MenuItem value="approved">Approved</MenuItem>
                        <MenuItem value="enrolled">Enrolled</MenuItem>
                        <MenuItem value="rejected">Rejected</MenuItem>
                        <MenuItem value="payment_pending">Payment pending</MenuItem>
                    </Select>
                </FormControl>
                <Button onClick={loadApplications} disabled={loading}>
                    Refresh
                </Button>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Ref #</TableCell>
                                <TableCell>Applicant</TableCell>
                                <TableCell>Class</TableCell>
                                <TableCell>Fee ({currencyCode})</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Submitted</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        No applications yet
                                    </TableCell>
                                </TableRow>
                            ) : (
                                rows.map((row) => (
                                    <TableRow key={row._id} hover>
                                        <TableCell>{row.applicationNumber}</TableCell>
                                        <TableCell>
                                            {row.lastname} {row.othernames}
                                        </TableCell>
                                        <TableCell>{row.classApplied}</TableCell>
                                        <TableCell>
                                            {row.feeGhs != null ? formatFee(row.feeGhs) : '—'}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={statusLabel(row.status)}
                                                color={STATUS_COLORS[row.status] || 'default'}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {row.createdAt
                                                ? new Date(row.createdAt).toLocaleDateString()
                                                : '—'}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Button size="small" onClick={() => openDetail(row)}>
                                                View
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <ApplicationDetailDialog
                open={detailOpen}
                application={selected}
                loading={detailLoading}
                reviewNotes={reviewNotes}
                onReviewNotesChange={setReviewNotes}
                actionLoading={actionLoading}
                onClose={closeDetail}
                onMarkUnderReview={() => runReview('under_review')}
                onReject={() => setRejectOpen(true)}
                onApprove={() => runReview('approve')}
                onEnroll={runEnroll}
                onCopyText={copyText}
                formatFee={formatFee}
            />

            <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)}>
                <DialogTitle>Reject application</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Reason (optional)"
                        fullWidth
                        multiline
                        minRows={3}
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectOpen(false)}>Cancel</Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={() => runReview('reject', { rejectionReason: rejectReason })}
                        disabled={actionLoading}
                    >
                        Reject
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={!!snack}
                autoHideDuration={6000}
                onClose={() => setSnack(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="info" onClose={() => setSnack(null)}>
                    {snack}
                </Alert>
            </Snackbar>
        </Box>
    );
}
